import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { NotebookPen, Pencil } from "lucide-react";
import { toast } from "sonner";

export const VisitNote = ({ patientId, event, index, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(event.note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/patients/${patientId}/history/${event.id}/note`, { note: text });
      onSaved(res.data.note);
      setEditing(false);
      toast.success("Treatment note saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence mode="wait" initial={false}>
      {editing ? (
        <motion.div key="edit" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-2 space-y-2">
          <Textarea
            data-testid={`visit-note-input-${index}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Archwire changed to 0.016 NiTi, elastics advised"
            className="text-sm min-h-[70px]"
            autoFocus
          />
          <div className="flex gap-2">
            <Button data-testid={`visit-note-save-${index}`} size="sm" disabled={saving} onClick={save} className="h-7 rounded-lg bg-teal-600 hover:bg-teal-700 text-white">
              {saving ? "Saving…" : "Save note"}
            </Button>
            <Button data-testid={`visit-note-cancel-${index}`} size="sm" variant="ghost" className="h-7 rounded-lg" onClick={() => { setText(event.note || ""); setEditing(false); }}>
              Cancel
            </Button>
          </div>
        </motion.div>
      ) : event.note ? (
        <motion.div key="view" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 group rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-2">
          <div className="flex items-start gap-2">
            <NotebookPen className="h-3.5 w-3.5 text-teal-600 mt-0.5 shrink-0" />
            <p data-testid={`visit-note-text-${index}`} className="text-xs text-slate-700 whitespace-pre-wrap flex-1">{event.note}</p>
            <button data-testid={`visit-note-edit-${index}`} onClick={() => setEditing(true)} className="text-slate-400 hover:text-teal-600 transition-colors">
              <Pencil className="h-3 w-3" />
            </button>
          </div>
        </motion.div>
      ) : (
        <motion.button
          key="add"
          data-testid={`visit-note-add-${index}`}
          onClick={() => setEditing(true)}
          className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-teal-700 hover:text-teal-900 transition-colors"
        >
          <NotebookPen className="h-3 w-3" /> Add treatment note
        </motion.button>
      )}
    </AnimatePresence>
  );
};
